import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowLeft,
    Banknote,
    Minus,
    Plus,
    ShoppingBag,
    Star,
    ZoomIn,
    ZoomOut,
} from "lucide-react";
import { toast } from "sonner";

import { fetchProduct, fetchSuggestions } from "@/lib/api";
import { productSubcategories } from "@/lib/product";
import usePageMeta from "@/hooks/usePageMeta";
import { useCart } from "@/context/CartContext";
import { useCatalog } from "@/hooks/useCatalog";
import { ProductVisual } from "@/components/ProductVisual";
import ProductCard from "@/components/ProductCard";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const SITE_URL = "https://atelierax.ma";

const resolvePhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith("http://") || photo.startsWith("https://")) return photo;
    const base = BACKEND_URL.replace(/\/$/, "");
    const path = photo.startsWith("/") ? photo : `/${photo}`;
    return `${base}${path}`;
};

const toAbsoluteUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${SITE_URL}${url}`;
};

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [qty, setQty] = useState(1);
    const [photoIndex, setPhotoIndex] = useState(0);
    const [selectedModel, setSelectedModel] = useState("");
    const [zoomed, setZoomed] = useState(false);
    const [pan, setPan] = useState({ x: 50, y: 50 });
    const photoFrameRef = useRef(null);
    const { addItem, setOpen: setCartOpen } = useCart();
    const { data: catalogData } = useCatalog();

    const { data: product, isLoading, error } = useQuery({
        queryKey: ["product", id],
        queryFn: () => fetchProduct(id),
        retry: 1,
    });

    const models = productSubcategories(product);

    // Loupe zoom: the image is scaled around the point under the cursor, so
    // moving the mouse (or dragging a finger) pans across the enlarged photo.
    const handleFramePointerMove = (e) => {
        if (!zoomed || !photoFrameRef.current) return;
        const rect = photoFrameRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setPan({
            x: Math.min(100, Math.max(0, x)),
            y: Math.min(100, Math.max(0, y)),
        });
    };

    // Reset the model choice per product; preselect when there is exactly one model.
    useEffect(() => {
        setSelectedModel(models.length === 1 ? models[0] : "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const notFound = error?.response?.status === 404;

    // Meta description : début de la vraie description produit (≤155 car.) si
    // disponible, sinon construction depuis tagline/specs/prix (fallback).
    const productDescription = (p) => {
        if (!p) return "Détail produit Atelier AX.";
        const cap = (s) =>
            s.length <= 155 ? s : `${s.slice(0, 152).replace(/[\s.,;:]+$/, "")}…`;
        if (p.description) return cap(p.description.trim());
        const args = "Prix en DH, livraison 24-72h partout au Maroc, paiement à la livraison.";
        const price = p.price_from ? `Dès ${p.price_from} DH.` : "";
        const base = [price, p.tagline, ...(p.specs || []).slice(0, 2)]
            .filter(Boolean)
            .join(" ");
        return cap(`${base} ${args}`.trim());
    };

    usePageMeta({
        title: product
            ? `${product.name} – Atelier AX Maroc`
            : "Produit — Atelier AX",
        description: productDescription(product),
        path: `/produit/${id}`,
    });

    // « Vous allez sans doute aimer aussi » : suggestions DÉTERMINISTES calculées
    // côté backend — score de pertinence (même marque, modèles compatibles,
    // affinité de catégorie, prix proche, bonus bestsellers), aucun aléatoire
    // (voir backend/server.py, route /api/products/{id}/suggestions).
    // Fallback documenté : si l'endpoint échoue ou renvoie moins de 4 produits,
    // on complète avec les premiers produits de la même catégorie dans l'ordre
    // du catalogue (hors produit courant et doublons) ; si toujours vide, la
    // section est masquée. NB : hooks placés avant les early returns.
    const { data: suggestionsData } = useQuery({
        queryKey: ["suggestions", id],
        queryFn: () => fetchSuggestions(id),
        enabled: !!product,
        staleTime: 5 * 60 * 1000,
        retry: 1,
    });

    const suggestions = useMemo(() => {
        const fromApi = suggestionsData?.items || [];
        if (!product) return fromApi;
        const pool = fromApi.filter((p) => p.id !== product.id);
        if (pool.length < 4) {
            const already = new Set(pool.map((p) => p.id));
            const sameCategory = (catalogData?.items || []).filter(
                (p) =>
                    p.id !== product.id &&
                    p.category === product.category &&
                    !already.has(p.id),
            );
            pool.push(...sameCategory.slice(0, 4 - pool.length));
        }
        return pool.slice(0, 4);
    }, [suggestionsData, catalogData, product]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#FBFBFD] flex items-center justify-center">
                <div className="w-8 h-8 rounded-full border-2 border-[#d4af37] border-t-transparent animate-spin" />
            </div>
        );
    }

    if (notFound || !product) {
        return (
            <div className="min-h-screen bg-[#FBFBFD] text-neutral-900 flex flex-col items-center justify-center px-6 text-center">
                <p className="font-mono-tech text-sm text-neutral-500 mb-3">404</p>
                <h1 className="font-display text-3xl font-semibold tracking-tight mb-4">
                    Produit introuvable
                </h1>
                <p className="text-neutral-600 mb-8 max-w-md">
                    Ce produit n'existe pas ou n'est plus disponible.
                </p>
                <Link to="/" className="dp-btn dp-btn-primary">
                    Retour au catalogue
                </Link>
            </div>
        );
    }

    const photos = (product.photos || []).map(resolvePhotoUrl).filter(Boolean);
    const mainPhoto = photos[photoIndex] || photos[0] || null;
    // JSON-LD : la vraie description si disponible, sinon tagline + specs.
    const description =
        product.description ||
        `${product.tagline}${
            product.specs?.length ? `. ${product.specs.join(". ")}` : ""
        }`;
    const numericPrice = product.price_from && !Number.isNaN(Number(product.price_from))
        ? Number(product.price_from)
        : null;

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description,
        image: photos.map(toAbsoluteUrl).filter(Boolean),
        brand: { "@type": "Brand", name: "Atelier AX" },
        url: `${SITE_URL}/produit/${product.id}`,
        offers: {
            "@type": "Offer",
            url: `${SITE_URL}/produit/${product.id}`,
            priceCurrency: "MAD",
            availability: "https://schema.org/InStock",
            ...(numericPrice ? { price: numericPrice } : {}),
            // Merchant listings (Search Console) — valeurs confirmées par le
            // propriétaire : livraison 30 DH (< 300 DH), gratuite dès 300 DH,
            // sous 24 à 72 h partout au Maroc.
            shippingDetails: {
                "@type": "OfferShippingDetails",
                shippingDestination: {
                    "@type": "DefinedRegion",
                    addressCountry: "MA",
                },
                shippingRate: {
                    "@type": "MonetaryAmount",
                    value: "30",
                    currency: "MAD",
                },
                freeShippingThreshold: {
                    "@type": "MonetaryAmount",
                    value: "300",
                    currency: "MAD",
                },
                deliveryTime: {
                    "@type": "ShippingDeliveryTime",
                    handlingTime: {
                        "@type": "QuantitativeValue",
                        minValue: 0,
                        maxValue: 1,
                        unitCode: "DAY",
                    },
                    transitTime: {
                        "@type": "QuantitativeValue",
                        minValue: 1,
                        maxValue: 3,
                        unitCode: "DAY",
                    },
                },
            },
            // Politique confirmée par le propriétaire : pas de retour sauf produit
            // défectueux (relevé de la garantie, pas du retour classique).
            hasMerchantReturnPolicy: {
                "@type": "MerchantReturnPolicy",
                applicableCountry: "MA",
                returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
        },
    };

    const handleAdd = () => {
        if (models.length > 0 && !selectedModel) {
            toast.error("Veuillez choisir un modèle avant d'ajouter au panier.");
            return;
        }
        addItem(product, qty, selectedModel || null);
        toast.success(
            `${qty} × ${product.name}${selectedModel ? ` (${selectedModel})` : ""} ajouté au panier`,
            {
                action: { label: "Voir panier", onClick: () => setCartOpen(true) },
            },
        );
    };

    // « Acheter maintenant » : même ajout au panier (incrémente si déjà présent),
    // puis ouverture directe du panier pour compléter la demande via WhatsApp.
    const handleBuyNow = () => {
        if (models.length > 0 && !selectedModel) {
            toast.error("Veuillez choisir un modèle avant d'acheter.");
            return;
        }
        addItem(product, qty, selectedModel || null);
        setCartOpen(true);
    };

    return (
        <div className="min-h-screen bg-[#FBFBFD] text-neutral-900 antialiased">
            <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

            <main className="max-w-[1320px] mx-auto px-6 md:px-10 py-8 md:py-14">
                {/* Breadcrumb */}
                <nav aria-label="Fil d'Ariane" className="mb-8 flex flex-wrap items-center gap-2 text-[13px] text-neutral-500">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="hover:text-black transition-colors inline-flex items-center gap-1.5"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Retour
                    </button>
                    <span aria-hidden="true">/</span>
                    <Link to="/" className="hover:text-black transition-colors">
                        Catalogue
                    </Link>
                    {product.category && product.category_id && (
                        <>
                            <span aria-hidden="true">/</span>
                            <Link
                                to={`/categorie/${encodeURIComponent(product.category_id)}`}
                                className="hover:text-black transition-colors truncate max-w-[30vw]"
                            >
                                {product.category}
                            </Link>
                        </>
                    )}
                    <span aria-hidden="true">/</span>
                    <span className="text-neutral-800 truncate max-w-[40vw]">{product.name}</span>
                </nav>

                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                    {/* Photos */}
                    <div className="lg:max-w-[440px] w-full">
                        <div
                            ref={photoFrameRef}
                            onPointerMove={handleFramePointerMove}
                            onClick={() => {
                                if (zoomed) setZoomed(false);
                            }}
                            style={{
                                touchAction: zoomed ? "none" : undefined,
                                cursor: zoomed ? "zoom-out" : "zoom-in",
                            }}
                            className="relative w-full aspect-[4/5] rounded-2xl bg-[#F5F5F7] overflow-hidden"
                        >
                            {mainPhoto ? (
                                <img
                                    src={mainPhoto}
                                    alt={product.name}
                                    decoding="async"
                                    fetchpriority="high"
                                    className="w-full h-full object-contain"
                                    style={{
                                        transform: zoomed ? "scale(2.5)" : "scale(1)",
                                        transformOrigin: `${pan.x}% ${pan.y}%`,
                                        transition: "transform 0.15s ease-out",
                                    }}
                                />
                            ) : (
                                <ProductVisual kind={product.kind} />
                            )}
                            {product.top_selling && (
                                <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[10px] font-mono-tech tracking-[0.12em] uppercase text-white bg-gradient-to-r from-[#5b21b6] to-[#d4af37] shadow-md">
                                    <Star className="w-3 h-3 fill-white" />
                                    Top vente
                                </span>
                            )}
                            {mainPhoto && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setZoomed((z) => !z);
                                    }}
                                    aria-label={zoomed ? "Réduire la photo" : "Zoomer la photo"}
                                    className="absolute left-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/30 text-white transition-colors hover:bg-black/60"
                                >
                                    {zoomed ? <ZoomOut className="w-4 h-4" /> : <ZoomIn className="w-4 h-4" />}
                                </button>
                            )}
                        </div>
                        {photos.length > 1 && (
                            <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                                {photos.map((url, i) => (
                                    <button
                                        key={url + i}
                                        type="button"
                                        onClick={() => {
                                            setPhotoIndex(i);
                                            setZoomed(false);
                                        }}
                                        aria-label={`Photo ${i + 1}`}
                                        className={`shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${
                                            i === photoIndex
                                                ? "border-[#d4af37]"
                                                : "border-transparent hover:border-black/20"
                                        }`}
                                    >
                                        <img src={url} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Info — panel premium : card blanche, radius généreux, ombre douce
                        (esprit Casetify, charte Atelier AX). Sur mobile le panel passe
                        sous la photo (grid empilée), sans toucher à la loupe zoom. */}
                    <div className="flex flex-col rounded-[24px] border border-black/[0.06] bg-white p-6 md:p-10 shadow-[0_24px_60px_-20px_rgba(28,28,30,0.18),0_2px_8px_rgba(28,28,30,0.04)]">
                        {product.category && (
                            <p className="font-mono-tech text-[11px] tracking-[0.18em] uppercase text-[#7c3aed] mb-3">
                                {product.category}
                                {models.length > 0 &&
                                    ` · ${models.slice(0, 3).join(", ")}${
                                        models.length > 3 ? ` +${models.length - 3}` : ""
                                    }`}
                            </p>
                        )}
                        <h1 className="font-display text-3xl md:text-[42px] leading-[1.08] tracking-tight font-semibold">
                            {product.name}
                        </h1>
                        <p className="mt-3 text-[15px] md:text-base text-neutral-500">{product.tagline}</p>

                        <ul className="mt-8 space-y-2.5 text-[13.5px] text-neutral-700 font-mono-tech">
                            {(product.specs || []).map((s) => (
                                <li key={s} className="flex items-center gap-2.5">
                                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: product.accent }} />
                                    {s}
                                </li>
                            ))}
                        </ul>

                        <div className="mt-8 flex items-baseline gap-3">
                            {product.original_price && (
                                <span className="text-sm text-neutral-400 line-through tabular-nums">
                                    {product.original_price} DH / د.م.
                                </span>
                            )}
                            <span className="text-2xl font-bold tabular-nums">
                                {product.price_from && product.price_from !== "Sur devis"
                                    ? `${product.price_from} DH / د.م.`
                                    : "Sur devis"}
                            </span>
                        </div>

                        <div className="mt-4 inline-flex items-center gap-2 self-start rounded-full border border-emerald-600/20 bg-emerald-50 px-3.5 py-1.5 text-[12.5px] font-medium text-emerald-800">
                            <Banknote className="w-4 h-4 text-emerald-600" />
                            Mode de paiement : Paiement à la livraison
                        </div>

                        {models.length > 0 && (
                            <div className="mt-8 max-w-xs space-y-1.5">
                                <Label className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">
                                    Modèle / compatibilité
                                </Label>
                                <Select value={selectedModel} onValueChange={setSelectedModel}>
                                    <SelectTrigger className="rounded-full bg-white">
                                        <SelectValue
                                            placeholder={
                                                models.length > 1
                                                    ? `Choisir parmi ${models.length} modèles`
                                                    : "Choisir le modèle"
                                            }
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {models.map((m) => (
                                            <SelectItem key={m} value={m}>
                                                {m}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-1 border border-black/10 rounded-full overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setQty((v) => Math.max(1, v - 1))}
                                    aria-label="Diminuer la quantité"
                                    className="w-10 h-11 flex items-center justify-center hover:bg-black/[0.04] transition"
                                >
                                    <Minus className="w-4 h-4" />
                                </button>
                                <span className="min-w-[28px] text-center text-[15px] font-medium tabular-nums">{qty}</span>
                                <button
                                    type="button"
                                    onClick={() => setQty((v) => Math.min(99, v + 1))}
                                    aria-label="Augmenter la quantité"
                                    className="w-10 h-11 flex items-center justify-center hover:bg-black/[0.04] transition"
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={handleAdd}
                                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white text-[14px] font-medium py-3 px-6 hover:bg-emerald-700 active:bg-emerald-800 transition-colors"
                            >
                                <ShoppingBag className="w-4 h-4" />
                                Ajouter au panier
                            </button>
                        </div>

                        {/* Acheter maintenant : ajoute au panier puis ouvre le drawer
                            pour envoyer la demande via WhatsApp (paiement à la livraison). */}
                        <button
                            type="button"
                            onClick={handleBuyNow}
                            data-testid="buy-now"
                            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1C1C1E] text-white text-[15px] font-semibold py-3.5 px-6 shadow-[0_10px_24px_-8px_rgba(28,28,30,0.5)] hover:bg-black hover:shadow-[0_14px_28px_-8px_rgba(28,28,30,0.55)] active:scale-[0.99] transition-all"
                        >
                            Acheter maintenant
                        </button>

                        {/* Description unique du produit (phase 3) ; fallback sur
                            le texte générique si la fiche n'a pas encore de texte. */}
                        {product.description ? (
                            <p className="mt-8 text-[14px] text-neutral-600 leading-relaxed border-t border-black/5 pt-6">
                                {product.description}
                            </p>
                        ) : (
                            <p className="mt-8 text-[13px] text-neutral-500 leading-relaxed border-t border-black/5 pt-6">
                                Produit sélectionné par Atelier AX — accessoires mobiles au Maroc.
                                Prix et disponibilité confirmés sur devis.
                            </p>
                        )}
                        <p className="mt-3 text-[12px] text-neutral-400 leading-relaxed">
                            Référence : <span className="font-mono-tech">{product.id}</span>
                        </p>
                    </div>
                </div>

                {/* Suggestions : 4 produits pertinents, score déterministe
                    calculé côté backend (aucun aléatoire). */}
                {suggestions.length > 0 && (
                    <section className="mt-16 md:mt-24" aria-label="Suggestions">
                        <div className="flex items-end justify-between gap-4">
                            <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-[#1C1C1E]">
                                Vous allez sans doute aimer aussi
                            </h2>
                            <Link
                                to="/produits"
                                className="shrink-0 text-[13px] font-medium text-neutral-500 hover:text-black transition-colors"
                            >
                                Voir tout le catalogue →
                            </Link>
                        </div>
                        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-x-4 sm:gap-x-6 md:gap-x-8 gap-y-10 sm:gap-y-12">
                            {suggestions.map((p, i) => (
                                <ProductCard key={p.id} product={p} index={i} />
                            ))}
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
};

export default ProductDetail;
